/*
 * Developed for EPSON AMERICA INC. by Softeq Development Corporation
 * http://www.softeq.com
 */

import React from 'react'
import { Field } from 'redux-form'
import classNames from 'classnames/bind'
import { MAX_LENGTHS } from 'constants/forms'
import { Text as TextType, GetTextPath } from 'hocs/localization'
import { Input, Captcha } from 'apps/FormFields'
import Button from 'elements/Button'
import {
  createValidation,
  createValidator,
  hasValidEmail,
  hasRequired,
  removeSpaces,
} from 'utils/form'
import * as styles from './LoginForm.scss'
import LoginMeta from './apps/LoginMeta'

const cx = classNames.bind(styles)

interface IProps {
  t: TextType,
  getTextPath: GetTextPath,
  handleSubmit: any,
  valid: boolean,
  submitting: boolean,
  isResendingEmail: boolean,
  onResend: () => void,
  isCaptchaVerified: boolean,
  onVerifyCaptcha: (response: string) => void,
  captchaRef: (ref: React.ReactElement<any>) => React.Ref<any>
  onSubmit: (formValues: any) => void,
  captchaFieldName: string,
  isCaptchaEnabled: boolean,
  isCaptchaWhiteListed: boolean,
  isShowEmailConfirmationLink: boolean,
  setScrollElementId: (elementId: string | null) => void,
}

class LoginForm extends React.Component<IProps, {}> {
  private requiredFieldValidation = createValidation([
    createValidator(hasRequired, this.props.getTextPath('errors.textField')),
  ])

  public emailValidation = createValidation([
    createValidator(hasValidEmail, this.props.getTextPath('errors.email')),
  ])

  public render () {
    const {
      t,
      handleSubmit,
      submitting,
      onVerifyCaptcha,
      captchaRef,
      onSubmit,
      onResend,
      captchaFieldName,
      isCaptchaEnabled,
      isCaptchaWhiteListed,
      isShowEmailConfirmationLink,
      isResendingEmail
    } = this.props

    return (
      <div>
        <div className={styles.loginTitle}>
          <h2>{t('header')}</h2>
        </div>
        <form className={styles.root} onSubmit={handleSubmit(onSubmit)}>
          <Field
            disabled={submitting}
            id='email'
            name='email'
            type='text'
            component={Input}
            validate={[this.requiredFieldValidation, this.emailValidation]}
            maxLength={MAX_LENGTHS.email}
            normalize={removeSpaces}
            label={t('emailPlaceholder')}
            required
          />
          <Field
            disabled={submitting}
            id='password'
            name='password'
            type='password'
            validate={this.requiredFieldValidation}
            component={Input}
            maxLength={MAX_LENGTHS.password}
            label={t('passwordPlaceholder')}
            required
            newLineError
          />
          {(isShowEmailConfirmationLink) && (
            <div className={styles.root}>
              <div className={styles.emailConfirm}>
                {t('profile.view.notifications.notConfirmedEmail')}
              </div>
              <div className={styles.emailConfirmLink}>
                <a
                  href='#'
                  onClick={onResend}
                  className={cx({ disabledLink: isResendingEmail })}
                >
                  {t('profile.view.notifications.resendEmailLink')}
                </a>
              </div>
            </div>
            )
          }
          {(isCaptchaEnabled && !isCaptchaWhiteListed) && (
            <div className={cx(styles.centered, styles.formField)}>
              <Field
                id='captcha'
                captchaRef={captchaRef}
                name={captchaFieldName}
                component={Captcha}
                verifyCallback={onVerifyCaptcha}
              />
            </div>
          )}
          <div className={cx(styles.formField, styles.NextButton, styles.SaveChange, 'text-center')}>
            <div className={styles.buttonWrap}>
              <Button
                fluid
                disabled={submitting}
              >
                {submitting ? t('submitButtonSubmitting') : t('submitButton')}
              </Button>
            </div>
          </div>
          <LoginMeta />
        </form>
      </div>
    )
  }
}

export default LoginForm
